import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es as esLocale } from "date-fns/locale";
import {
  Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench,
  ArrowLeft, ArrowRight, Check, ImagePlus, X, CalendarDays, Loader2,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import SEO from "../components/SEO";
import { useLang, CATEGORIES } from "../i18n";
import { Wordmark, CheckMark } from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const iconMap = { Wind, WashingMachine, Paintbrush, DoorOpen, Droplets, SprayCan, Wrench };

const slide = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

export default function Booking() {
  const { t, lang } = useLang();
  const b = t.booking;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preCat = params.get("category");

  const [step, setStep] = useState(preCat && CATEGORIES.some((c) => c.id === preCat) ? 1 : 0);
  const [data, setData] = useState({
    categoryId: preCat && CATEGORIES.some((c) => c.id === preCat) ? preCat : "",
    description: "",
    photo: null,
    date: null,
    name: "",
    phone: "",
    address: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const category = CATEGORIES.find((c) => c.id === data.categoryId);
  const categoryLabel = category ? t.services.items[category.id].name : "";

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set({ photo: reader.result });
    reader.readAsDataURL(file);
  };

  const canProceed = useMemo(() => {
    if (step === 0) return !!data.categoryId;
    if (step === 2) return !!data.date;
    if (step === 3) return data.name && data.phone && data.address && emailValid(data.email);
    return true;
  }, [step, data]);

  const next = () => {
    if (step === 3) return submit();
    if (!canProceed) {
      toast.error(b.required);
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => (step === 0 ? navigate("/") : setStep((s) => s - 1));

  const submit = async () => {
    if (!data.name || !data.phone || !data.address) {
      toast.error(b.required);
      return;
    }
    if (!emailValid(data.email)) {
      toast.error(b.invalidEmail);
      return;
    }
    toast.dismiss();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, {
        category_id: data.categoryId,
        category_label: categoryLabel,
        description: data.description,
        photo_base64: data.photo,
        preferred_date: format(data.date, "yyyy-MM-dd"),
        name: data.name,
        phone: data.phone,
        address: data.address,
        email: data.email,
        lang,
      });
      toast.dismiss();
      setStep(4);
    } catch (e) {
      toast.error(b.errorTitle, { description: b.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-crema flex flex-col">
      <SEO
        title={lang === "es" ? "Reserva tu handyman — He Meant To" : "Book a handyman — He Meant To"}
        description={lang === "es"
          ? "Reserva un handyman de He Meant To en Florida Central y Orlando en 5 pasos. Elige categoría, describe el problema y agenda."
          : "Book a He Meant To handyman in Central Florida and Orlando in 5 quick steps. Pick a category, describe the problem, and schedule."}
        path="/book"
        lang={lang}
        noindex
      />
      {/* top bar */}
      <div className="border-b border-charcoal/10">
        <div className="mx-auto max-w-3xl px-5 md:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="booking-home">
            <Wordmark iconSize={24} />
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-warmgray hover:text-charcoal transition-colors"
            aria-label="Close"
            data-testid="booking-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* progress */}
      {step < 4 && (
        <div className="mx-auto max-w-3xl w-full px-5 md:px-8 pt-8">
          <div className="flex items-center gap-2" data-testid="booking-progress">
            {b.steps.slice(0, 4).map((label, i) => (
              <div key={i} className="flex-1">
                <div
                  className={`h-1 rounded-full transition-colors duration-500 ${
                    i <= step ? "bg-teal" : "bg-charcoal/10"
                  }`}
                />
                <span className={`mt-2 block text-[11px] font-semibold ${i <= step ? "text-charcoal" : "text-warmgray/60"}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 mx-auto max-w-3xl w-full px-5 md:px-8 py-10 md:py-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={slide}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* STEP 0 — category */}
            {step === 0 && (
              <div data-testid="booking-step-category">
                <StepHead title={b.s1.title} sub={b.s1.sub} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {CATEGORIES.map((c) => {
                    const Icon = iconMap[c.icon] || Wrench;
                    const active = data.categoryId === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => set({ categoryId: c.id })}
                        className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                          active
                            ? "border-teal bg-teal/5 ring-1 ring-teal"
                            : "border-charcoal/10 bg-white hover:border-teal/40"
                        }`}
                        data-testid={`cat-option-${c.id}`}
                      >
                        <Icon className={`h-6 w-6 mb-3 ${active ? "text-teal" : "text-charcoal/70"}`} strokeWidth={1.8} />
                        <span className="block font-display font-bold text-charcoal leading-tight">
                          {t.services.items[c.id].name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 1 — describe */}
            {step === 1 && (
              <div data-testid="booking-step-describe">
                <StepHead title={b.s2.title} sub={b.s2.sub} />
                <Textarea
                  value={data.description}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder={b.s2.placeholder}
                  rows={5}
                  className="text-base bg-white border-charcoal/15 focus-visible:ring-teal"
                  data-testid="booking-description"
                />
                <div className="mt-5">
                  {data.photo ? (
                    <div className="relative inline-block">
                      <img src={data.photo} alt="upload" className="h-32 w-32 object-cover rounded-xl" />
                      <button
                        onClick={() => set({ photo: null })}
                        className="absolute -top-2 -right-2 grid place-items-center h-7 w-7 rounded-full bg-charcoal text-crema"
                        data-testid="booking-photo-remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      className="inline-flex items-center gap-2.5 rounded-xl border border-dashed border-charcoal/25 px-5 py-4 cursor-pointer hover:border-teal transition-colors text-warmgray hover:text-charcoal"
                      data-testid="booking-photo-label"
                    >
                      <ImagePlus className="h-5 w-5" />
                      <span className="text-sm font-medium">{b.s2.photo}</span>
                      <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="booking-photo-input" />
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 — date */}
            {step === 2 && (
              <div data-testid="booking-step-date">
                <StepHead title={b.s3.title} sub={b.s3.sub} />
                <div className="flex justify-center rounded-2xl border border-charcoal/10 bg-white p-4">
                  <Calendar
                    mode="single"
                    selected={data.date}
                    onSelect={(d) => set({ date: d })}
                    disabled={{ before: new Date() }}
                    locale={lang === "es" ? esLocale : undefined}
                    data-testid="booking-calendar"
                  />
                </div>
                {data.date && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal px-4 py-2 text-sm font-semibold">
                    <CalendarDays className="h-4 w-4" />
                    {format(data.date, "PPP", { locale: lang === "es" ? esLocale : undefined })}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — contact */}
            {step === 3 && (
              <div data-testid="booking-step-contact">
                <StepHead title={b.s4.title} sub={b.s4.sub} />
                <div className="grid gap-4">
                  <Field label={b.s4.name} value={data.name} onChange={(v) => set({ name: v })} testid="booking-name" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={b.s4.phone} value={data.phone} onChange={(v) => set({ phone: v })} type="tel" testid="booking-phone" />
                    <Field
                      label={b.s4.email}
                      value={data.email}
                      onChange={(v) => set({ email: v })}
                      type="email"
                      testid="booking-email"
                      error={data.email && !emailValid(data.email) ? b.invalidEmail : ""}
                    />
                  </div>
                  <Field label={b.s4.address} value={data.address} onChange={(v) => set({ address: v })} testid="booking-address" />
                </div>
              </div>
            )}

            {/* STEP 4 — confirmation */}
            {step === 4 && (
              <div className="text-center py-6" data-testid="booking-confirmation">
                <motion.div
                  initial={{ scale: 0, rotate: -12 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                  className="mx-auto grid place-items-center h-20 w-20 rounded-2xl bg-teal mb-8"
                >
                  <Check className="h-10 w-10 text-crema" strokeWidth={3} />
                </motion.div>
                <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-charcoal">
                  {b.s5.title}
                </h1>
                <p className="mt-4 text-warmgray text-lg">{b.s5.sub}</p>

                <div className="mt-8 mx-auto max-w-sm rounded-2xl border border-charcoal/10 bg-white p-6 text-left">
                  <Row label={b.s5.category} value={categoryLabel} />
                  <div className="h-px bg-charcoal/10 my-3" />
                  <Row
                    label={b.s5.date}
                    value={data.date ? format(data.date, "PPP", { locale: lang === "es" ? esLocale : undefined }) : ""}
                  />
                </div>
                <p className="mt-6 text-sm text-warmgray max-w-md mx-auto flex items-center justify-center gap-2">
                  <CheckMark size={18} /> {b.s5.note}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate("/")}
                    className="rounded-full bg-charcoal text-crema font-semibold px-6 py-3.5 hover:bg-coral transition-colors"
                    data-testid="booking-back-home"
                  >
                    {b.s5.home}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* footer nav */}
      {step < 4 && (
        <div className="sticky bottom-0 border-t border-charcoal/10 bg-crema/90 backdrop-blur">
          <div className="mx-auto max-w-3xl px-5 md:px-8 py-4 flex items-center justify-between">
            <button
              onClick={back}
              className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal font-semibold transition-colors"
              data-testid="booking-back"
            >
              <ArrowLeft className="h-4 w-4" /> {b.back}
            </button>
            <button
              onClick={next}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-coral text-crema font-semibold pl-6 pr-5 py-3 hover:brightness-105 transition-all disabled:opacity-60"
              data-testid="booking-next"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {b.submitting}
                </>
              ) : (
                <>
                  {step === 3 ? b.submit : b.next}
                  {step !== 3 && <ArrowRight className="h-4 w-4" />}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepHead({ title, sub }) {
  return (
    <div className="mb-8">
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-charcoal">{title}</h1>
      <p className="mt-3 text-warmgray text-base md:text-lg">{sub}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, error }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-charcoal mb-2">{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-12 text-base bg-white focus-visible:ring-teal ${
          error ? "border-coral focus-visible:ring-coral" : "border-charcoal/15"
        }`}
        data-testid={testid}
      />
      {error && (
        <span className="mt-1.5 block text-xs font-medium text-coral" data-testid={`${testid}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-warmgray text-sm">{label}</span>
      <span className="font-semibold text-charcoal">{value}</span>
    </div>
  );
}
